import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { useI18n } from '../i18n/I18nProvider';

/**
 * Сессия админки: кто вошёл (GET /admin/me) и в каком кабинете он работает.
 * - Владелец (superadmin) — консоль платформы; может открыть кабинет любой компании (режим просмотра).
 * - Админ/наблюдатель компании — только кабинет своей компании.
 * Выбранная компания передаётся на сервер заголовком X-Admin-Tenant-ID (services/api.js).
 */
const SessionContext = createContext(null);

const VIEW_AS_KEY = 'adminViewAsCompany';
const TENANT_HEADER_KEY = 'adminSelectedTenant';

function readViewAs() {
  try {
    const raw = localStorage.getItem(VIEW_AS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Контекст организации для запросов (заголовок X-Admin-Tenant-ID)
function setRequestTenant(id) {
  const value = id ? String(id) : 'all';
  try { localStorage.setItem(TENANT_HEADER_KEY, value); } catch { /* приватный режим */ }
}

export function SessionProvider({ children }) {
  const [state, setState] = useState({ loading: true, admin: null, tenant: null, error: null });
  const [viewAs, setViewAs] = useState(readViewAs);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/me');
      setState({ loading: false, admin: data.admin, tenant: data.tenant, error: null });
    } catch (err) {
      setState({ loading: false, admin: null, tenant: null, error: err });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const role = state.admin?.role ?? null;
  const isOwner = role === 'superadmin';
  const isViewer = role === 'org_viewer';

  // Компания, чей кабинет открыт: своя для ролей компании, выбранная — для владельца
  const company = isOwner ? viewAs : state.tenant;

  const { setCurrency } = useI18n();
  useEffect(() => { setCurrency(company?.wallet_currency || 'UAH'); }, [company?.wallet_currency, setCurrency]);

  useEffect(() => {
    if (!state.admin) return;
    setRequestTenant(isOwner ? viewAs?.id : null);
  }, [state.admin, isOwner, viewAs]);

  const enterCompany = useCallback((tenant) => {
    const next = { id: tenant.id, name: tenant.name, company_code: tenant.company_code ?? null, wallet_currency: tenant.wallet_currency || 'UAH', timezone: tenant.timezone || null };
    setViewAs(next);
    try { localStorage.setItem(VIEW_AS_KEY, JSON.stringify(next)); } catch { /* приватный режим */ }
    setRequestTenant(next.id);
  }, []);

  const exitCompany = useCallback(() => {
    setViewAs(null);
    try { localStorage.removeItem(VIEW_AS_KEY); } catch { /* приватный режим */ }
    setRequestTenant(null);
  }, []);

  const signOut = useCallback(async () => {
    try { await api.post('/admin/logout'); } catch { /* сессия уже недействительна */ }
    for (const key of [VIEW_AS_KEY, TENANT_HEADER_KEY]) {
      try { localStorage.removeItem(key); } catch { /* приватный режим */ }
    }
    window.location.href = '/login';
  }, []);

  const value = useMemo(() => ({
    ...state,
    role,
    isOwner,
    isViewer,
    canEdit: !isViewer,
    company,
    isViewingAsOwner: isOwner && !!viewAs,
    enterCompany,
    exitCompany,
    signOut,
    reload: load,
  }), [state, role, isOwner, isViewer, company, viewAs, enterCompany, exitCompany, signOut, load]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside SessionProvider');
  return ctx;
}
